<template>
  <nav class="tabs-view" :class="{ collapsed: showCollapsed }" :aria-label="navLabel" >
    <div ref="tabs" class="tabs">
      <tabs-view-tab v-for="tab of tabs" :key="tab.key" :label="tab.label" :to="tab.to" :current="isTabCurrent(tab)" :class="tab.class ? tab.class : {}"></tabs-view-tab>
      <!-- single underline that slides to the active tab (tabs are variable width, so it is measured from the DOM) -->
      <div class="tabs-indicator" :class="{ 'no-anim': !indicatorReady }" :style="indicatorStyle" aria-hidden="true"></div>
    </div>
    <div class="collapsed" v-if="showCollapsed">
      <ion-item lines="none" >
        <div class="select-container" @click="openSelect">

          <ion-label v-if="label">{{ label }}</ion-label>
          <ion-select  id="nav-select" :value="activeTab.key" interface="default" :aria-label="navLabel" @ionChange="selectChanged" :toggleIcon="chevronExpandOutline">

            <ion-select-option v-for="tab of tabs" :key="tab.key" :to="tab.to" :class="tab.class ? tab.class : {}" :value="tab.key">{{
              tab.label
            }}</ion-select-option>
        </ion-select>
      </div>
      </ion-item>
    </div>
  </nav>
</template>

<script setup>
import { chevronExpandOutline} from "ionicons/icons";
import { IonSelect, IonSelectOption, IonLabel, IonItem } from "@ionic/vue";
import TabsViewTab from "./TabsViewTab.vue";
</script>
<script>
export default {
  
  name: "tabs-view",
  data() {
    return {
      showCollapsed: false,
      indicatorLeft: 0,
      indicatorWidth: 0,
      indicatorVisible: false,
      // skips the slide transition on the very first positioning (avoids a slide-in from the left on load)
      indicatorReady: false,
    };
  },
  props: ["tabs", "label"],
  computed: {
    activeTab() {
      for (let tab of this.tabs) {
        let resolved = this.$router.resolve(tab.to);
        if (resolved && this.$route.fullPath == resolved.fullPath) {
          return tab;
        }
      }
      return this.tabs && this.tabs.length > 0 ? this.tabs[0] : null;
    },
    indicatorStyle() {
      return {
        width: this.indicatorWidth + "px",
        transform: "translateX(" + this.indicatorLeft + "px)",
        opacity: this.indicatorVisible ? 1 : 0,
      };
    },
    navLabel() {
      return this.label || this.$t("menu-category-navigation-aria-label");
    },
  },
  mounted: function () {
    this.refresh();
  },
  watch: {
    $route: function () {
      this.refresh();
    },
    tabs: function () {
      // tabs load/change asynchronously (categories, pages, …) — reposition once rendered
      this.refresh();
    },
  },
  methods: {
    openSelect(){
      let navSelect = document.getElementById("nav-select");
      // console.log('navSelect',navSelect)
      navSelect.open()

    },
    selectChanged(e) {
      //dont change if the current route is not pinting to the menu. fi shopping cart
      // if (this.$route.name != "menu") {        
      //   return;
      // }
      let value = e.detail && e.detail.value ? e.detail.value : "everything";
      
      for (let tab of this.tabs) {
        if (tab.key == value) {
          this.$router.push(tab.to);
          break;
        }
      }
    },
    // mirrors the visual underline rule: a tab is current when it has the `active` class,
    // or (when it is not a parent tab) when its link is the exact-active route.
    isTabCurrent(tab) {
      let cls = tab.class || {};
      if (cls.active) return true;
      if (cls.parent) return false;
      let resolved = this.$router.resolve(tab.to);
      return !!(resolved && this.$route.fullPath === resolved.fullPath);
    },
    refresh() {
      this.$nextTick(() => this.checkSize());
    },
    checkSize() {
      let container = this.$refs["tabs"];
      if (!container) {
        return;
      }

      // measure the tab elements only — the indicator is also a child of .tabs
      let tabEls = container.querySelectorAll(".tabs-view-tab");
      if (tabEls.length) {
        let firstTop = tabEls[0].offsetTop;
        let lastTop = tabEls[tabEls.length - 1].offsetTop;
        this.showCollapsed = lastTop != firstTop;
      }

      // reposition after showCollapsed-driven layout settles
      this.$nextTick(() => this.updateIndicator());
    },
    updateIndicator() {
      let container = this.$refs["tabs"];
      if (!container) {
        return;
      }

      // the highlighted tab is either flagged with the `active` class or is the exact-active router link
      let activeEl = container.querySelector(".tabs-view-tab.active");
      if (!activeEl) {
        let link = container.querySelector(".tabs-view-tab:not(.parent) .tab.router-link-exact-active");
        activeEl = link ? link.closest(".tabs-view-tab") : null;
      }
      if (!activeEl) {
        this.indicatorVisible = false;
        return;
      }

      // underline tracks the link box; inset 20% on each side to match the previous static underline
      let target = activeEl.querySelector(".tab") || activeEl;
      let containerRect = container.getBoundingClientRect();
      let targetRect = target.getBoundingClientRect();
      let inset = targetRect.width * 0.2;

      this.indicatorLeft = targetRect.left - containerRect.left + inset;
      this.indicatorWidth = Math.max(0, targetRect.width - inset * 2);
      this.indicatorVisible = true;

      if (!this.indicatorReady) {
        this.$nextTick(() => {
          this.indicatorReady = true;
        });
      }
    },
  },

  created() {
    window.addEventListener("resize", this.checkSize);
  },
  unmounted() {
    window.removeEventListener("resize", this.checkSize);
  },
};
</script>
<style scoped>
.tabs-view {
  margin-top: 0.3rem;

  border-bottom: 1px solid var(--ion-color-light-shade);
}

.tabs-view .tabs {
  display: flex;
  justify-content: center;
  padding: 0 1rem;
  overflow: hidden;
  margin-bottom: -1px;
  flex-wrap: wrap;
  position: relative;
}

.tabs-indicator {
  position: absolute;
  left: 0;
  bottom: 0;
  height: 2px;
  background-color: var(--ion-color-primary);
  transition: transform 0.25s ease, width 0.25s ease, opacity 0.2s ease;
  will-change: transform, width;
  pointer-events: none;
}
.tabs-indicator.no-anim {
  transition: none;
}
.tabs-view.collapsed {
  margin-top: 0;
}
.collapsed .tabs {
  position: absolute;
  height: 0;
  visibility: hidden;
}
.select-container{
    
    width: 100%;
    display: flex;
    justify-content: center;
}

.select-container ion-select{
  flex:0;
  margin-right:-10px ;
}
</style>